import { Flex } from "@eworkit/iris";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { Header } from "@/components/Header";
import { UserContext } from "@/components/User";
import { logout } from "@/data/session";
import { convertUser } from "@/data/users";
import { IDialogModal, inDialogModal } from "@/eworkitComponents";
import { SessionContext } from "@/infrastructure/Session";
import { CloseButton } from "@/views/components/CloseButton";
import { ICreateCompanyFormValues } from "@/views/components/Company/Create";
import { StyledModal } from "@/views/components/Modal";
import { useToasts } from "@/views/components/Toast/useToasts";
import { router } from "@/views/router";
import "./_delete-account.scss";

import ApplicationsSection from "./Applications/ApplicationsSection";
import CompanySection from "./Company/CompanySection";
import ConfirmSection from "./Confirm/ConfirmSection";

interface IConvertModalProps extends IDialogModal {
  onClose: (shouldRefresh?: boolean) => void;
}

interface IModalProps extends IConvertModalProps {
  onConvertAccount: (values: ICreateCompanyFormValues) => Promise<void>;
}

const Modal = ({ onClose, onConvertAccount }: IModalProps) => {
  const { t } = useTranslation("session");

  const [companyId, setCompanyId] = useState(0);

  const [currentStep, setCurrentStep] = useState(1);

  const renderStep = (step: number) => {
    switch (step) {
      case 1:
        return <ConfirmSection updateStep={setCurrentStep} onClose={onClose} />;
      case 2:
        return (
          <CompanySection
            onClose={onClose}
            updateStep={setCurrentStep}
            setCompanyId={setCompanyId}
            onConvertAccount={onConvertAccount}
          />
        );
      case 3:
        return <ApplicationsSection onClose={onClose} companyId={companyId} />;
      default:
        return <ConfirmSection updateStep={setCurrentStep} onClose={onClose} />;
    }
  };

  return (
    <StyledModal>
      <Flex flexDirection="column" p="md">
        <Flex justifyContent="space-between">
          <Header weight="bold">{t(`USERS.SETTINGS.USER_ACCOUNT.CONVERT_PROFILE.CONFIRM_DIALOG.HEADER`)}</Header>

          <CloseButton onClick={() => onClose()} />
        </Flex>

        {renderStep(currentStep)}
      </Flex>
    </StyledModal>
  );
};

const ConvertProfileModalComponent = inDialogModal(Modal);

export const ConvertProfileModal = React.memo(({ open, onClose }: IConvertModalProps) => {
  const { addToast } = useToasts();
  const User = useContext(UserContext);
  const Session = useContext(SessionContext);
  const history = useHistory();

  const { t } = useTranslation(["session", "common"]);

  const convertAccount = async (values: ICreateCompanyFormValues) => {
    try {
      await convertUser({ profileId: User.contextConsultantProfile?.id ?? 0 }, values);

      logout();
      Session.destroy(() => {
        history.push(router.get("converted-account").path);
      });
    } catch (response) {
      addToast({
        text: t("common:ERROR_MESSAGES.GENERAL"),
        type: "warning",
      });
    } finally {
      onClose(true);
    }
  };

  if (open) {
    return <ConvertProfileModalComponent onClose={onClose} onConvertAccount={convertAccount} open={open} size="xs" />;
  }

  return null;
});
